import type {
  IntegrationTool,
  IntegrationToolMethod,
} from '@/features/Integrations/types/integration';

export const TOOL_METHODS: readonly IntegrationToolMethod[] = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

/** O que o diálogo de tool `api` edita. O resto da definição passa adiante intacto. */
export interface ApiToolDraft {
  name: string;
  description: string;
  method: IntegrationToolMethod;
  endpoint: string;
  /** O JSON Schema como texto — só vira objeto ao salvar, depois de validado. */
  inputSchema: string;
}

export type ApiToolProblem =
  | 'nameRequired'
  | 'nameInvalid'
  | 'nameTaken'
  | 'endpointRequired'
  | 'schemaInvalidJson'
  | 'schemaNotObject';

/** A mesma regra do `updateToolsValidator` da api: letras, números, `_`, `.` e `-`, até 128. */
const TOOL_NAME_RE = /^[A-Za-z0-9_.-]{1,128}$/;

const EMPTY_SCHEMA = { type: 'object', properties: {} };

export function emptyApiToolDraft(): ApiToolDraft {
  return {
    name: '',
    description: '',
    method: 'GET',
    endpoint: '',
    inputSchema: JSON.stringify(EMPTY_SCHEMA, null, 2),
  };
}

export function toolToDraft(tool: IntegrationTool): ApiToolDraft {
  return {
    name: tool.name,
    description: tool.description ?? '',
    method: tool.method ?? 'GET',
    endpoint: tool.endpoint ?? '',
    inputSchema: JSON.stringify(tool.inputSchema ?? EMPTY_SCHEMA, null, 2),
  };
}

/** O schema em texto: objeto JSON, ou o motivo de não ser. Vazio = schema sem parâmetros. */
export function parseInputSchema(
  text: string,
): { ok: true; value: Record<string, unknown> } | { ok: false; problem: ApiToolProblem } {
  if (!text.trim()) return { ok: true, value: { ...EMPTY_SCHEMA } };
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    // JSON inválido é resposta, não falha: vira o aviso do campo.
    return { ok: false, problem: 'schemaInvalidJson' };
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { ok: false, problem: 'schemaNotObject' };
  }
  return { ok: true, value: parsed as Record<string, unknown> };
}

/**
 * Problemas do rascunho. `originalName` é o nome antes da edição — renomear para o próprio
 * nome não é colisão.
 */
export function validateApiToolDraft(
  draft: ApiToolDraft,
  existing: readonly Pick<IntegrationTool, 'name'>[],
  originalName?: string,
): ApiToolProblem[] {
  const problems: ApiToolProblem[] = [];
  const name = draft.name.trim();
  if (!name) problems.push('nameRequired');
  else if (!TOOL_NAME_RE.test(name)) problems.push('nameInvalid');
  else if (name !== originalName && existing.some((tool) => tool.name === name)) {
    problems.push('nameTaken');
  }
  // Endpoint vazio chama a URL base e existe em tools de catálogo; só a tool NOVA precisa dele.
  if (originalName === undefined && !draft.endpoint.trim()) problems.push('endpointRequired');
  const schema = parseInputSchema(draft.inputSchema);
  if (!schema.ok) problems.push(schema.problem);
  return problems;
}

/**
 * Aplica o rascunho à lista: substitui a tool `originalName` (mantendo a posição e os campos
 * que o diálogo não edita, como `intents` e `body_template`) ou acrescenta no fim.
 * Supõe o rascunho já validado.
 */
export function upsertApiTool(
  tools: readonly IntegrationTool[],
  draft: ApiToolDraft,
  originalName?: string,
): IntegrationTool[] {
  const schema = parseInputSchema(draft.inputSchema);
  const patch = {
    name: draft.name.trim(),
    description: draft.description.trim(),
    method: draft.method,
    endpoint: draft.endpoint.trim(),
    inputSchema: schema.ok ? schema.value : { ...EMPTY_SCHEMA },
  };
  const index = originalName ? tools.findIndex((tool) => tool.name === originalName) : -1;
  if (index < 0) {
    // `intents` é obrigatório na definição REST do runtime; tool nova nasce sem nenhum.
    return [...tools, { intents: [], ...patch }];
  }
  return tools.map((tool, i) => (i === index ? { ...tool, ...patch } : tool));
}

export function removeApiTool(tools: readonly IntegrationTool[], name: string): IntegrationTool[] {
  return tools.filter((tool) => tool.name !== name);
}
